import { useEffect, useState } from "react";
import { CheckCircle2, Smartphone, Monitor, Download, Loader2, Save, AlertCircle, Share2, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";
import { downloadFile, withRef } from "@/lib/appresse";

const HIDE_KEY = "appresse_hide_download_info";
const APP_URL = "https://apppresse.com";
// The link is written INSIDE the sentence (not only as a preview) so that even
// if the recipient removes the rich preview (Apple's × in Messages, which no web
// app can disable), the address stays written in the message text.
const APP_SHARE_TEXT = "Découvrez APP PRESSE sur apppresse.com, le kiosque numérique de toute la presse française 📰";

const detectDevice = () => {
  const ua = (navigator.userAgent || "").toLowerCase();
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (/macintosh/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
};

const DEVICE_INFO = {
  android: {
    icon: Smartphone,
    label: "Sur votre Android",
    steps: (
      <>
        Ouvrez l'application <span className="font-semibold text-foreground">« Fichiers »</span> ou
        <span className="font-semibold text-foreground"> « Mes fichiers »</span>, puis le dossier
        <span className="font-semibold text-foreground"> Téléchargements</span>.
      </>
    ),
  },
  desktop: {
    icon: Monitor,
    label: "Sur votre ordinateur",
    steps: (
      <>
        Ouvrez le dossier <span className="font-semibold text-foreground">Téléchargements</span> ou la
        barre de téléchargements de votre navigateur.
      </>
    ),
  },
};

// Global download experience: loading animation (progress %), then a device-aware
// confirmation. On iOS the user saves via the native share sheet (no dead-end).
export const DownloadInfoModal = () => {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("loading"); // loading | done | ready-ios | error
  const [title, setTitle] = useState("");
  const [device, setDevice] = useState("desktop");
  const [url, setUrl] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [blob, setBlob] = useState(null);
  const [fileHref, setFileHref] = useState("");
  const [editionId, setEditionId] = useState("");
  const [filename, setFilename] = useState("revue.pdf");
  const [progress, setProgress] = useState(0);
  const [dontShow, setDontShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onEvt = (e) => {
      const d = e?.detail || {};
      if (d.phase === "close") {
        setOpen(false);
        return;
      }
      setPhase(d.phase || "loading");
      if (d.title !== undefined) setTitle(d.title || "");
      if (d.url !== undefined) setUrl(d.url || "");
      if (d.shareUrl !== undefined) setShareUrl(d.shareUrl || "");
      if (d.blob !== undefined) setBlob(d.blob || null);
      if (d.fileHref !== undefined) setFileHref(d.fileHref || "");
      if (d.id !== undefined) setEditionId(d.id || "");
      if (d.filename) setFilename(d.filename);
      if (typeof d.progress === "number") setProgress(d.progress);
      setDevice(detectDevice());
      if (d.phase === "loading") {
        setDontShow(false);
        setSaving(false);
      }
      setOpen(true);
    };
    window.addEventListener("appresse:download-info", onEvt);
    return () => window.removeEventListener("appresse:download-info", onEvt);
  }, []);

  const info = DEVICE_INFO[device] || DEVICE_INFO.desktop;
  const Icon = info.icon;

  const persistHide = () => {
    if (dontShow) {
      try {
        localStorage.setItem(HIDE_KEY, "1");
      } catch (e) {}
    }
  };

  const cleanupUrl = () => {
    if (url && url.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    }
  };

  const close = () => {
    persistHide();
    setOpen(false);
    cleanupUrl();
  };

  // Share the APP PRESSE link so recipients can install/open the kiosk too.
  const shareApp = async () => {
    const data = { title: "APP PRESSE", text: APP_SHARE_TEXT, url: withRef(APP_URL) };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
    } catch (e) {
      return; // user cancelled the share sheet
    }
    try {
      await navigator.clipboard.writeText(withRef(APP_URL));
      toast.success("Lien de l'application copié !");
    } catch (e) {}
  };

  // iOS: save the PDF into Files. The file is fetched lazily here (only when the
  // user explicitly asks to save it), then handed to the native share sheet which
  // offers "Enregistrer dans Fichiers".
  const onIosSave = async () => {
    persistHide();
    setSaving(true);
    try {
      let b = blob;
      if (!b && fileHref) {
        let lastErr;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const res = await fetch(fileHref);
            if (!res.ok) throw new Error("http " + res.status);
            b = await res.blob();
            setBlob(b);
            break;
          } catch (e) {
            lastErr = e;
            if (attempt < 3) await new Promise((r) => setTimeout(r, 800 * attempt));
          }
        }
        if (!b) throw lastErr || new Error("network");
      }
      const file = b ? new File([b], filename, { type: "application/pdf" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: title || filename });
        setSaving(false);
        setOpen(false);
        return;
      }
      // Fallback (older iOS without file sharing): save via a download link.
      if (b) {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { try { a.remove(); URL.revokeObjectURL(u); } catch (e) {} }, 4000);
      }
      setSaving(false);
      setOpen(false);
    } catch (e) {
      setSaving(false);
      // AbortError = the user simply cancelled the iOS share sheet → stay silent.
      if (e && e.name === "AbortError") return;
      toast.error("Connexion instable. Vérifiez votre réseau et réessayez.");
    }
  };

  // Retry a failed revue download from the error screen.
  const retryDownload = () => {
    if (editionId) downloadFile(editionId, title);
  };

  // iOS: share the revue as the SINGLE rich APP PRESSE card (cover + branding),
  // via a link to /api/share/{id} whose Open Graph preview is that card.
  const onIosShare = async () => {
    persistHide();
    const link = withRef(shareUrl || url);
    try {
      if (navigator.share) {
        await navigator.share({ title: title || "APP PRESSE", text: APP_SHARE_TEXT, url: link });
        setOpen(false);
        cleanupUrl();
        return;
      }
    } catch (e) {
      return; // user cancelled
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Lien de la revue copié !");
    } catch (e) {}
    setOpen(false);
  };

  const ShareAppLink = () => (
    <button
      type="button"
      onClick={shareApp}
      className="mx-auto mt-3 flex items-center gap-1.5 text-sm font-semibold text-primary transition-opacity hover:opacity-70"
      data-testid="download-info-share-app"
    >
      <Share2 className="h-4 w-4" /> Partager l'application
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent
        className="max-w-sm rounded-3xl text-center border-0 shadow-2xl"
        data-testid="download-info-modal"
      >
        {/* ---------- LOADING ---------- */}
        {phase === "loading" && (
          <div className="py-2" data-testid="download-info-loading">
            <div className="mx-auto mb-4 relative flex h-16 w-16 items-center justify-center">
              <Loader2 className="h-16 w-16 animate-spin text-primary/25" />
              <Download className="absolute h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-lg font-bold">Chargement de la revue…</DialogTitle>
            {title && (
              <p className="mt-0.5 text-sm text-muted-foreground line-clamp-1">« {title} »</p>
            )}
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                style={{ width: `${Math.max(6, progress)}%` }}
                data-testid="download-info-progress"
              />
            </div>
            <p className="mt-2 text-xs font-mono text-muted-foreground">
              {progress > 0 ? `${progress}%` : "Préparation…"}
            </p>
          </div>
        )}

        {/* ---------- DONE (desktop / android) ---------- */}
        {phase === "done" && (
          <>
            <DialogHeader className="items-center">
              <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/15">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <DialogTitle className="text-xl font-bold">Revue téléchargée !</DialogTitle>
              {title && (
                <p className="text-sm font-medium text-muted-foreground line-clamp-2">« {title} »</p>
              )}
              <DialogDescription className="pt-1 text-[15px] leading-relaxed text-foreground/80">
                Voici où la retrouver :
              </DialogDescription>
            </DialogHeader>
            <div className="mt-1 flex items-start gap-3 rounded-2xl bg-muted/60 p-4 text-left">
              <Icon className="mt-0.5 h-6 w-6 shrink-0 text-primary" data-testid="download-info-device-icon" />
              <div className="text-sm">
                <p className="mb-0.5 font-semibold" data-testid="download-info-device-label">
                  {info.label}
                </p>
                <p className="text-muted-foreground">{info.steps}</p>
              </div>
            </div>
            <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={dontShow} onCheckedChange={(v) => setDontShow(v === true)} data-testid="download-info-dontshow-checkbox" />
              Ne plus afficher ce message
            </label>
            <ShareAppLink />
            <DialogFooter className="mt-2">
              <Button className="w-full rounded-full" onClick={close} data-testid="download-info-ok-button">
                J'ai compris
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ---------- READY (iOS) ---------- */}
        {phase === "ready-ios" && (
          <>
            <DialogHeader className="items-center">
              <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-500/15">
                <Share2 className="h-9 w-9 text-blue-600" />
              </div>
              <DialogTitle className="text-xl font-bold">Partagez votre revue</DialogTitle>
              {title && (
                <p className="text-sm font-medium text-muted-foreground line-clamp-2">« {title} »</p>
              )}
              <DialogDescription className="pt-1 text-[15px] leading-relaxed text-foreground/80">
                <span className="font-semibold text-foreground">Partager la revue</span> envoie la belle carte
                APP PRESSE (couverture + lien vers l'app). Vous pouvez aussi l'enregistrer dans Fichiers.
              </DialogDescription>
            </DialogHeader>
            <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={dontShow} onCheckedChange={(v) => setDontShow(v === true)} data-testid="download-info-dontshow-checkbox" />
              Ne plus afficher ce message
            </label>
            <DialogFooter className="mt-3 flex-col gap-2 sm:flex-col sm:space-x-0">
              <Button className="w-full rounded-full gap-2" onClick={onIosShare} data-testid="download-info-ios-share-button">
                <Share2 className="h-4 w-4" />
                Partager la revue
              </Button>
              <Button variant="outline" className="w-full rounded-full gap-2 text-muted-foreground" onClick={onIosSave} disabled={saving} data-testid="download-info-ios-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer le PDF dans Fichiers
              </Button>
              <Button variant="ghost" className="w-full rounded-full" onClick={close} data-testid="download-info-ok-button">
                Fermer
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ---------- ERROR ---------- */}
        {phase === "error" && (
          <>
            <DialogHeader className="items-center">
              <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/15">
                <AlertCircle className="h-9 w-9 text-amber-600" />
              </div>
              <DialogTitle className="text-xl font-bold">Connexion instable</DialogTitle>
              <DialogDescription className="pt-1 text-[15px] leading-relaxed text-foreground/80">
                Le réseau s'est interrompu pendant le chargement de la revue.
                Vérifiez votre connexion (Wi‑Fi / données), puis réessayez.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-3 flex-col gap-2 sm:flex-col sm:space-x-0">
              {editionId && (
                <Button className="w-full rounded-full gap-2" onClick={retryDownload} data-testid="download-info-retry-button">
                  <RefreshCw className="h-4 w-4" /> Réessayer
                </Button>
              )}
              <Button variant="ghost" className="w-full rounded-full" onClick={() => setOpen(false)} data-testid="download-info-ok-button">
                Fermer
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DownloadInfoModal;
